<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-row>
        <b-col cols="12">
          <b-card no-body>
            <template #header>
              Actuals/Estimates <ProjectCategoryFilter v-if="!isTv" v-model="categoryFilter" class="mb-0 ml-1"
                :defaultCategories="['FiX ', 'MAINT', 'Pipeline', 'T&M']">
              </ProjectCategoryFilter>
              <b-form-datepicker v-if="!isTv" class="inline-input"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" v-model="loggedAfter" />
              <div v-if="!isTv" class="pull-right ">
                <switches v-model="toggleShowChart" color="blue" text-enabled="Show table" text-disabled="Show chart" />
              </div>
            </template>
            <div v-if="spinners.contentIsLoading" class="text-center">
              <b-spinner type="grow" variant="primary" />
            </div>
            <div v-else>
              <b-row v-if="toggleShowChart && chartItems">
                <b-col cols="12">
                  <ProjectsChart :chartItems="chartItems" />
                </b-col>
              </b-row>
              <b-row v-else>
                <b-col cols="12">
                  <b-table class="table-sm text-center table-responsive mb-0 table-hover" responsive bordered
                    :fields="tableFields" :items="tableItems">

                    <template #thead-top="data">
                      <b-tr v-if="actions.see_prices">
                        <b-th colspan="4"></b-th>
                        <b-th colspan="3">Completition based</b-th>
                        <b-th colspan="3">Date based</b-th>
                      </b-tr>
                    </template>

                    <!-- baseline_costs ----------------------------------------------------------------------->
                    <template v-slot:head(name)="data">
                      Project Name
                    </template>
                    <template v-slot:cell(name)="row">
                      <router-link :to="{ name: 'ProjectHome', params: { jira_key: row.item.info.key, cat: 'epics' } }"><i
                          class="fa fa-bar-chart text-warning" aria-hidden="true"></i></router-link>
                      <router-link v-if="actions.see_prices"
                        :to="{ name: 'ProjectHome', params: { jira_key: row.item.info.key, cat: 'finances' } }">
                        <i class="fa fa-table text-success" aria-hidden="true"></i>
                      </router-link>
                      <b-link :to="{ name: 'ProjectHome', params: { jira_key: row.item.info.key } }">
                        {{ row.item.name }}
                      </b-link>
                    </template>

                    <!-- baseline_costs ----------------------------------------------------------------------->
                    <template v-slot:head(baseline_costs)="data">
                      <div>Baseline</div>
                      <div><small>Contract Total</small></div>
                    </template>
                    <template v-slot:cell(baseline_costs)="row">
                      <b-tooltip :target="`budgets-cell${row.item.info.id}`" triggers="hover">
                        <div>
                          {{ row.item.info.baseline_costs / 100 | numFormat('0,0') }},
                          {{ row.item.info.contract_budget / 100 | numFormat('0,0') }} |
                          {{ Math.round(row.item.info.ratio * 10) / 10 }} |
                          {{ Math.round(row.item.info.ratio_todate * 10) / 10 }}
                        </div>
                        <div v-for="e in row.item.info.change_requests" :key="e.id">
                          {{ e.baseline_costs / 100 | numFormat('0,0') }},
                          {{ e.contract_budget / 100 | numFormat('0,0') }} |
                          {{ Math.round(e.ratio * 10) / 10 }} |
                          {{ Math.round(e.ratio_todate * 10) / 10 }}
                        </div>
                      </b-tooltip>
                      <span :id="`budgets-cell${row.item.info.id}`">
                        <span :class="{
                          'text-zero': row.item.baseline_total === 0
                        }">{{ (row.item.baseline_total) | numFormat('0,0') }} EUR </span>
                        <div>
                          <small :class="['text-secondary', {
                            'text-zero': row.item.contract_total === 0
                          }]"> {{ row.item.contract_total | numFormat('0,0') }} EUR</small>
                        </div>
                        <div v-if="row.item.additional_actuals * row.item.additional_actuals > 0"><small
                            :class="['text-danger']"> {{ row.item.additional_actuals | numFormat('0,0') }} EUR</small>
                        </div>
                      </span>
                    </template>

                    <!-- actuals ----------------------------------------------------------------------->
                    <template v-slot:head(actuals)>
                      <div>Actual cost</div>
                      <div><small>Over baseline cost</small></div>
                      <div><small>Logged time</small></div>
                    </template>
                    <template v-slot:cell(actuals)="row">
                      <b-tooltip :target="`actuals-cell${row.item.info.id}`" triggers="hover">
                        <div>
                          {{ $dt('dash-main.actuals-tooltip.actuals-worked', 'Actuals worked:') }}
                          {{ row.item.actuals_plain | numFormat('0,0') }} EUR
                        </div>
                        <div>
                          {{ $dt('dash-main.actuals-tooltip.additional-actuals', 'Additional actuals:') }}
                          {{ row.item.additional_actuals_totals | numFormat('0,0') }} EUR
                        </div>
                      </b-tooltip>
                      <span :id="`actuals-cell${row.item.info.id}`" :class="{
                        'text-muted': row.item.baseline_total === 0,
                        'text-warning': row.item.baseline_total > 0 && (row.item.actuals > row.item.baseline_total && row.item.actuals < row.item.contract_total),
                        'text-danger': row.item.baseline_total > 0 && (row.item.actuals >= row.item.contract_total),
                      }">{{ row.item.actuals | numFormat('0,0') }} EUR</span>
                      <div v-if="row.item.baseline_total > 0 && row.item.actuals > row.item.baseline_total">
                        <small class="text-danger">
                          {{ (row.item.actuals - row.item.baseline_total) | numFormat('0,0') }} EUR
                        </small>
                      </div>
                      <div><small class="text-secondary">{{ Math.round(row.item.actuals_time / 3600) }} h</small></div>
                    </template>

                    <!-- last_date ----------------------------------------------------------------------->
                    <template v-slot:head(last_date)>
                      <div>Last time logged/Sync</div>
                      <div><small>Ref.From (if set)</small></div>
                      <div><small>Ref.End (if set)</small></div>
                    </template>
                    <template v-slot:cell(last_date)="{ item }">
                      <div>{{ item.last_date }}
                        <i :class="[
                          'fa', 'fa-refresh', {
                            'fa-spin text-danger': syncingProjects.includes(item.id),
                            'text-warning': !syncingProjects.includes(item.id),
                          }]" aria-hidden="true" @click="if (!syncingProjects.includes(item.id)) syncProject(item.id)">
                        </i>
                      </div>
                      <div v-if="item.info.reference_start_date">
                        <b-badge>Ref.From: {{ item.info.reference_start_date.format("DD.MM.YYYY") }}</b-badge>
                      </div>
                      <div v-if="item.info.reference_end_date">
                        <b-badge :variant="item.info.reference_end_date < new Date() ? 'danger' : 'secondary'">
                          Ref.End: {{ item.info.reference_end_date.format("DD.MM.YYYY") }}
                        </b-badge>
                      </div>
                    </template>

                    <!-- completition (completition based) ----------------------------------------------------------------------->
                    <template v-slot:head(completition)="data">
                      <div>Completition %</div>
                      <div><small>Spent days</small></div>
                    </template>
                    <template v-slot:cell(completition)="row">
                      <span :class="{
                          'text-zero': row.item.info.estimated_completition === 0
                        }
                          ">{{ (row.item.info.estimated_completition) }}</span>
                      <div><small :class="['text-secondary', {
                          'text-zero': Math.round(getEtcTime(row.item) / 3600 / 8) === 0
                        }]
                          ">{{ Math.round(getEtcTime(row.item) / 3600 / 8) }} days</small>
                      </div>
                    </template>

                    <!-- eac (completition based)----------------------------------------------------------------------->
                    <template v-slot:head(eac)="data">
                      <div>ETC</div>
                      <div>EAC</div>
                    </template>
                    <template v-slot:cell(eac)="row">
                      <div :class="[{ 'text-zero': getEtcMoney(row.item) < 1 }, 'd-flex justify-content-between']">
                        <span>
                          <span v-if="isEtcBasedOnActuals(row.item)" v-b-tooltip.hover title="Based on actuals"
                            class="text-secondary">BA</span>
                        </span>
                        <span v-b-tooltip.hover title="ETC">{{ Math.round(getEtcMoney(row.item)) | numFormat('0,0') }}
                          EUR</span>
                      </div>
                      <div :class="[{
                          'text-muted': getEacMoney(row.item) < 1 || row.item.contract_total < 10,
                          'text-warning': row.item.contract_total && getEacMoney(row.item) > 0 && (getEacMoney(row.item) > row.item.baseline_total && getEacMoney(row.item) < row.item.contract_total),
                          'text-danger': row.item.contract_total && getEacMoney(row.item) > 0 && (getEacMoney(row.item) >= row.item.contract_total),
                        }]
                          " v-b-tooltip.hover title="EAC">
                        {{ Math.round(getEacMoney(row.item)) | numFormat('0,0') }} EUR
                      </div>
                    </template>

                    <!-- completition_profit (completition based)----------------------------------------------------------------------->
                    <template v-slot:head(completition_profit)="data">
                      <div>Profit Margin %</div>
                      <div><small>Profit at end</small></div>
                      <small>Profit margin goal</small>
                    </template>
                    <template v-slot:cell(completition_profit)="{ item }">
                      <div v-if="getEacMoney(item) > 0 && item.contract_total > 0" v-b-tooltip.hover title="Profit at end"
                        :class="['text-secondary', {
                            'text-success': item.contract_total - getEacMoney(item) >= 0,
                            'text-danger': Math.round(item.contract_total - getEacMoney(item)) < 0,
                          }]
                            ">
                        <div>{{ item.compl_profitp | numFormat('0,0') }} %</div>
                        {{ item.compl_profit | numFormat('0,0') }} EUR
                      </div>
                      <div><small>Goal: {{ item.info.profit_margin_goal }}%</small></div>
                    </template>

                    <!-- profit ----------------------------------------------------------------------->
                    <template v-slot:head(profit)>
                      <div>Contract to date<br>(or to Ref.end)</div>
                      <div><small>Profit to date</small></div>
                    </template>
                    <template v-slot:cell(profit)="{ item }">
                      <div>
                        <small v-b-tooltip.hover title="Contract to date">
                          {{ item.contract_total_todate | numFormat('0,0') }} EUR
                        </small>
                      </div>
                      <div :class="{
                          'text-success': item.profitp >= item.info.profit_margin_goal,
                          'text-warning': item.profitp < item.info.profit_margin_goal,
                          'text-danger': item.profit < 0,
                        }">
                        <div><small v-b-tooltip.hover title="Profit to date">{{ item.profit | numFormat('0,0') }}
                            EUR</small></div>
                      </div>
                    </template>

                    <!-- profit_eac ----------------------------------------------------------------------->
                    <template v-slot:head(profit_eac)="data">
                      <div>ETC</div>
                      <div>EAC</div>
                    </template>
                    <template v-slot:cell(profit_eac)="row">
                      <div :class="[{ 'text-zero': row.item.profit_etc < 1 }, 'd-flex justify-content-between']">
                        <span></span>
                        <span v-b-tooltip.hover title="ETC">{{ Math.round(row.item.profit_etc) | numFormat('0,0') }}
                          EUR</span>
                      </div>
                      <div :class="[{
                          'text-muted': row.item.profit_eac < 1 || row.item.contract_total < 10,
                          'text-warning': row.item.contract_total && row.item.profit_eac > 0 && (row.item.profit_eac > row.item.baseline_total && row.item.profit_eac < row.item.contract_total),
                          'text-danger': row.item.contract_total && row.item.profit_eac > 0 && (row.item.profit_eac >= row.item.contract_total),
                        }]
                          " v-b-tooltip.hover title="EAC">
                        {{ Math.round(row.item.profit_eac) | numFormat('0,0') }} EUR
                      </div>
                    </template>

                    <!-- profit_end ----------------------------------------------------------------------->
                    <template v-slot:head(profit_end)="data">
                      <div>Profit Margin %</div>
                      <div><small>Profit at end</small></div>
                      <div><small>Profit margin goal</small></div>
                    </template>
                    <template v-slot:cell(profit_end)="{ item }">
                      <div :class="{
                          'text-success': item.profitp >= item.info.profit_margin_goal,
                          'text-warning': item.profitp < item.info.profit_margin_goal,
                          'text-danger': item.profit < 0,
                        }">
                        <div>{{ item.profitp | numFormat('0,0') }} %</div>
                        <div><small>{{ item.profit_end | numFormat('0,0') }} EUR</small></div>
                      </div>
                      <div><small>Goal: {{ item.info.profit_margin_goal }}%</small></div>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Pagination from 'laravel-vue-pagination';
import Switches from 'vue-switches';
import moment from 'moment';
import Services from '@/services';
import statuses from '../../../store/statuses';
import ProjectCategoryFilter from '@/views/components/jira/ProjectCategoryFilter.vue';
import ProjectsChart from '@/views/components/dash/ProjectsChart';

export default {
  mounted() {
    this.getData();
  },
  props: ['page'],
  data() {
    return {
      loggedAfter: moment().add(-4, 'weeks').format('YYYY-MM-DD'),
      toggleShowChart: false,
      syncingProjects: [],
      actions: {},
      spinners: {
        contentIsLoading: true,
      },
      categoryFilter: {},
      actuals: [],
      projects: [],

    };
  },
  components: {
    // Pagination,
    ProjectCategoryFilter,
    ProjectsChart,
    Switches
  },
  computed: {
    ...mapGetters('Auth', ['isTv']),
    tableFields() {
      return [
        { key: 'name', label: 'Project name', class: 'text-left' },
        { key: 'last_date', label: 'Last time logged' },
        ...(this.actions.see_prices ? [
          { key: 'baseline_costs', label: 'baseline_costs', class: 'text-right' },
          { key: 'actuals', label: 'actuals', class: 'text-right' },
          { key: 'completition', label: 'completition', class: 'text-right' },
          { key: 'eac', label: 'Eac', class: 'text-right' },
          { key: 'completition_profit', label: 'Completition profit', class: 'text-right' },
          { key: 'profit', label: 'Profit', class: 'text-right' },
          { key: 'profit_eac', label: 'Profit EAC', class: 'text-right' },
          { key: 'profit_end', label: 'Profit', class: 'text-right' },
        ] : [])
      ];
    },
    tableItems() {
      return this.actuals.map((p) => {
        p.info = this.projects.find((tp) => tp.external_id === p.external_id) || {};
        p.change_req_baseline = p.info.change_requests.reduce((accum, e) => { return accum + e.ratio * e.baseline_costs; }, 0);
        p.change_req_additional_actuals = p.info.change_requests.reduce((accum, e) => { return accum + e.ratio * e.additional_actuals; }, 0);
        p.change_req_contract_budget = p.info.change_requests.reduce((accum, e) => { return accum + e.ratio * e.contract_budget; }, 0);
        p.change_req_contract_budget_todate = p.info.change_requests.reduce((accum, e) => { return accum + e.ratio_todate * e.contract_budget; }, 0);
        p.baseline_total = (p.info.baseline_costs * p.info.ratio + p.change_req_baseline) / 100;
        p.actuals_plain = p.actuals * 1;
        p.additional_actuals_totals = (p.info.additional_actuals * p.info.ratio + p.change_req_additional_actuals) / 100;
        // EUR Contract ammount proportional to time - from contract start to current day.
        // (change requests are included and calculated also from start to current day)
        // If from date is set, contract is cropped to start from set "From" date
        p.contract_total_todate = (p.info.contract_budget * p.info.ratio_todate + p.change_req_contract_budget_todate) / 100;
        // EUR labour + material costs to date
        p.actuals = p.actuals * 1 + p.additional_actuals_totals;

        // EUR Contract ammount proportional to time - from contract start to end of project.
        // (change requests are included and calculated also from start to current day)
        // If from date is set, contract is cropped to start from set "From" date
        p.contract_total = (p.info.contract_budget * p.info.ratio + p.change_req_contract_budget) / 100;
        // Profit EUR based on completition % entered by PM or fetched from JIRA
        p.compl_profit = Math.round(p.contract_total - this.getEacMoney(p));
        // Profit % based on completition % entered by PM or fetched from JIRA
        p.compl_profitp = (p.contract_total - this.getEacMoney(p)) / p.contract_total * 100;

        // Profit percentage
        p.profitp = ((p.contract_total_todate - p.actuals) / p.contract_total_todate) * 100;
        // Profit EUR
        p.profit = (p.contract_total_todate - p.actuals);
        // Estimated profit at end of project EUR - calculation based on profit %
        p.profit_end = p.contract_total * (p.profitp / 100.0);
        // Estimate at completition - calculation based on profit %
        p.profit_eac = (p.contract_total - p.profit_end);
        // Estimate to completition - calculation based on profit %
        p.profit_etc = p.profit_eac - p.actuals;
        return p;
      }).filter((p) => p.info.permission_scheme !== 'archived projects').filter((p) => this.categoryFilter[p.info.category_id]);
    },
    chartItems() {
      return this.actuals.map((p) => {
        p.info = this.projects.find((tp) => tp.external_id === p.external_id) || {};
        p.change_req_baseline = p.info.change_requests.reduce((accum, e) => { return accum + e.baseline_costs; }, 0);
        p.baseline_total = (p.info.baseline_costs + p.change_req_baseline) / 100;
        p.additional_actuals_totals = (p.info.additional_actuals + p.change_req_additional_actuals) / 100;
        p.change_req_contract_budget = p.info.change_requests.reduce((accum, e) => { return accum + e.contract_budget; }, 0);
        p.contract_total = (p.info.contract_budget + p.change_req_contract_budget) / 100;
        p.start_date = p.info.change_requests
          .concat({ start_date: p.info.start_date, end_date: p.info.end_date })
          .reduce((prev, e) => { return ((prev && (new Date(prev) < new Date(e.start_date))) || (prev && !e.start_date)) ? prev : e.start_date; }, null);
        p.end_date = p.info.change_requests
          .concat({ start_date: p.info.start_date, end_date: p.info.end_date })
          .reduce((prev, e) => { return ((prev && (new Date(prev) > new Date(e.end_date))) || (prev && !e.start_date)) ? prev : e.end_date; }, null);
        p.eac = Math.round(this.getEacMoney(p));
        return p;
      }).filter((p) => p.info.permission_scheme !== 'archived projects').filter((p) => this.categoryFilter[p.info.category_id]);
    }
  },
  watch: {
    loggedAfter() {
      this.getData();
    }
  },
  methods: {
    getData() {
      Services.dash.activeProjects({ logged_after: this.loggedAfter }).then(({ data }) => {
        this.actuals = data.actuals;
        this.actions = data.actions;
        this.projects = data.projects.map((p) => {
          p.reference_start_date = p.reference_start_date ? moment(p.reference_start_date) : null;
          p.reference_end_date = p.reference_end_date ? moment(p.reference_end_date) : null;
          p.estimated_end_date = p.estimated_end_date ? moment(p.estimated_end_date) : null;
          p = this.mapPeriodRatio('ratio', p.reference_start_date)(p);
          p = this.mapPeriodRatio('ratio_todate', p.reference_start_date, p.reference_end_date || moment())(p);
          p.change_requests = p.change_requests.map(this.mapPeriodRatio('ratio', p.reference_start_date));
          p.change_requests = p.change_requests.map(this.mapPeriodRatio('ratio_todate', p.reference_start_date, p.reference_end_date || moment()));
          return p;
        });
        this.spinners.contentIsLoading = false;
      });
    },
    mapPeriodRatio(field, referenceStartDate, referenceEndDate = null) {
      return (e) => {
        let ratio = 1.0;
        if (referenceStartDate && (referenceStartDate > moment(e.end_date))) {
          ratio = 0.0;
        } else if (referenceEndDate && (referenceEndDate < moment(e.start_date))) {
          ratio = 0.0;
        } else if (
          (referenceStartDate && (referenceStartDate < moment(e.end_date) && referenceStartDate >= moment(e.start_date)))
          || (referenceEndDate && (referenceEndDate > moment(e.start_date) && referenceEndDate <= moment(e.end_date)))
        ) {
          const rStart = referenceStartDate ? moment.max([referenceStartDate, moment(e.start_date)]) : moment(e.start_date);
          const rEnd = referenceEndDate ? moment.min([referenceEndDate, moment(e.end_date)]) : moment(e.end_date);

          ratio = rEnd.diff(rStart, 'days') / (moment(e.end_date).diff(moment(e.start_date), 'days') + 1);
          // ratio = moment(e.end_date).diff(referenceStartDate, 'days') / (moment(e.end_date).diff(moment(e.start_date), 'days') + 1);

          if (Number.isNaN(ratio)) {
            console.log('isnan', rStart, rEnd, rEnd.diff(rStart, 'days'));
          }
        }
        e[field] = ratio;
        return e;
      };
    },
    getEacMoney(item) {
      if (item.baseline_total < 10 && item.actuals < 10) return 0;
      return 1 * this.getEtcMoney(item) + 1 * item.actuals;
    },
    getEtcMoney(item) {
      if ((item.baseline_total < 10 && item.actuals < 10)) {
        // nothing to base estimate on
        return 0;
      } if (item.info.estimated_completition < 1) {
        return item.actuals < 10 ? item.baseline_total : 0;
      } if (item.actuals > 1) {
        // base on actuals
        return ((item.actuals / item.info.estimated_completition) * (100 - item.info.estimated_completition));
      }
      // base on baseline
      return ((item.baseline_total / item.info.estimated_completition) * (100 - item.info.estimated_completition));
    },
    isEtcBasedOnActuals(item) {
      if ((item.baseline_total < 10 && item.actuals < 10)) {
        return false;
      } if (item.info.estimated_completition < 1) {
        return false;
      } if (item.actuals > 1) {
        // base on actuals
        return true;
      }
      // base on baseline
      return false;
    },
    getEtcTime(item) {
      if ((item.baseline_total < 10 && item.actuals_time < 10)) {
        // nothing to base estimate on
        return 0;
      } if (item.info.estimated_completition < 1) {
        return item.actuals_time < 10 ? item.baseline_total : 0;
      } if (item.actuals_time > 1) {
        // base on actuals
        return ((item.actuals_time / item.info.estimated_completition) * (100 - item.info.estimated_completition));
      }
      // base on baseline
      return 0;
    },

    syncProject(id) {
      this.syncingProjects.push(id);
      Services.dash.syncProject({ id }).then(() => {
        this.syncingProjects = this.syncingProjects.filter((i) => i !== id);
      });
    }
  }
};
</script>

<style scoped lang="scss">
.text-muted {
  color: #bfbfbf;
}

.text-small {
  font-size: 0.8em;
}

.inline-input {
  display: inline-flex;
  width: 150px;
  margin-left: 0.2em;
  vertical-align: middle;
}
</style>
